// styles.ts — gemeinsame makeStyles für die Inline-Verknüpfungslisten.
// Spiegelt die lokalen Button-/Layout-Stile der Anlage-Forms (ghostBtn/removeBtn/
// rowActions) und ergänzt Such-/Chip-/Add-Stile im Refined-Classic-Look.

import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";

export const useRelStyles = makeStyles({
  // Container einer Kategorie-Liste (untereinander gestapelte Eintrags-Karten).
  list: { display: "flex", flexDirection: "column", rowGap: "14px" },
  emptyText: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    color: REFINED.ink3,
    fontStyle: "italic",
  },
  rowActions: { display: "flex", justifyContent: "flex-end", marginTop: "2px" },
  ghostBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "9px 16px",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "0.82rem",
    padding: "6px 12px",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  divider: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("20px", "0"),
    ...shorthands.borderWidth("0"),
  },
  itemTopBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: "4px",
  },
  // Kleine Unter-Bereichs-Überschrift in einer Eintrags-Karte.
  sectionLabel: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: REFINED.ink2,
  },
  hint: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    color: REFINED.ink2,
    lineHeight: 1.4,
  },

  // ── ContactRefField: ausgewählter Kontakt (Chip) ──────────────────────────
  chip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "10px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    ...shorthands.padding("9px", "12px"),
  },
  chipMain: { display: "flex", flexDirection: "column", rowGap: "2px", minWidth: 0 },
  chipName: { fontFamily: REFINED.fontUI, fontSize: "0.92rem", fontWeight: 600, color: REFINED.ink },
  chipType: { fontFamily: REFINED.fontUI, fontSize: "0.78rem", color: REFINED.ink3 },
  chipChange: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.82rem",
    cursor: "pointer",
    whiteSpace: "nowrap",
    ":hover": { textDecorationLine: "underline" },
  },

  chipActions: { display: "flex", alignItems: "center", columnGap: "14px", flexShrink: 0 },

  // ── ContactRefField: Suche ────────────────────────────────────────────────
  searchWrap: { position: "relative", display: "flex", alignItems: "center", width: "100%" },
  searchIcon: { position: "absolute", left: "12px", color: REFINED.ink3, display: "inline-flex", pointerEvents: "none" },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("11px", "13px", "11px", "36px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.95rem",
    color: REFINED.ink,
    backgroundColor: REFINED.white,
    outlineStyle: "none",
    "::placeholder": { color: REFINED.ink3 },
    ":hover": { ...shorthands.borderColor(REFINED.aub) },
    ":focus": { ...shorthands.borderColor(REFINED.aub), boxShadow: REFINED.focusRing },
  },
  results: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    ...shorthands.overflow("hidden"),
    maxHeight: "220px",
    overflowY: "auto",
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "10px",
    ...shorthands.border("0"),
    ...shorthands.borderBottom("1px", "solid", REFINED.aub50),
    backgroundColor: REFINED.white,
    ...shorthands.padding("9px", "12px"),
    textAlign: "left",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  resultName: { fontFamily: REFINED.fontUI, fontSize: "0.9rem", fontWeight: 600, color: REFINED.ink },
  resultType: { fontFamily: REFINED.fontUI, fontSize: "0.78rem", color: REFINED.ink3 },
  msg: { fontFamily: REFINED.fontUI, fontSize: "0.85rem", color: REFINED.ink3, padding: "2px 2px" },
  newLink: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.86rem",
    cursor: "pointer",
    alignSelf: "flex-start",
    ...shorthands.padding("2px", "0"),
    ":hover": { textDecorationLine: "underline" },
  },
  switchLink: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.ink2,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.82rem",
    cursor: "pointer",
    alignSelf: "flex-start",
    ...shorthands.padding("2px", "0"),
    ":hover": { color: REFINED.aub, textDecorationLine: "underline" },
  },
  refField: { display: "flex", flexDirection: "column", rowGap: "8px" },

  // Inline-Toggle-Zeile (z. B. intern/extern oder „im Haushalt").
  toggleRow: { display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" },
});
